import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

export const FAQ = ({ onOpenContact }) => {
  const { t } = useLanguage();

  const faqItems = [
    { q: t('faq.q1'), a: t('faq.a1') },
    { q: t('faq.q2'), a: t('faq.a2') },
    { q: t('faq.q3'), a: t('faq.a3') },
    { q: t('faq.q4'), a: t('faq.a4') },
    { q: t('faq.q5'), a: t('faq.a5') },
  ];

  return (
    <section 
      id="faq" 
      className="relative py-24 md:py-32 bg-white"
      data-testid="faq-section"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="inline-block font-mono text-xs uppercase tracking-[0.3em] text-[#008080] mb-4"
          >
            Support
          </motion.span>
          <h2 
            className="font-montserrat font-bold text-3xl md:text-4xl lg:text-5xl text-[#222222] tracking-tight"
            data-testid="faq-title"
          >
            {t('faq.title')}
          </h2>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-gray-200 rounded-xl px-6 data-[state=open]:border-[#008080]/30 data-[state=open]:shadow-lg transition-all duration-300"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger 
                  className="py-5 text-left font-montserrat font-semibold text-[#222222] hover:text-[#008080] transition-colors [&[data-state=open]]:text-[#008080]"
                  data-testid={`faq-trigger-${index}`}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 font-poppins text-[#64748B] leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <p className="font-poppins text-[#64748B] mb-4">
            {t('faq.stillHaveQuestions')}
          </p>
          <motion.button
            onClick={onOpenContact}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#008080] text-[#008080] rounded-full font-poppins font-semibold hover:bg-[#008080] hover:text-white transition-colors"
            data-testid="faq-contact-button"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            {t('faq.contactUs')}
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};
